import React from "react";
import { Flex, Stack, Wrap, WrapItem } from "@chakra-ui/react";

import Button from "../../../components/Button";
import { GifSelector } from "./GifSelector";
import { YTSelector } from "./YTSelector";
import { P } from "../../../components/Type";
import { TiktokSelector } from "./TiktokSelector";
import { ReelsSelector } from "./ReelsSelector";
import { VoiceNoteSelector } from "./VoiceNoteSelector";
import { ImageGuessSelector } from "./ImageGuessSelector";
import { Trans } from "@lingui/macro";
import { FaMoneyBills } from "react-icons/fa6";
import MinAmountInfo from "../../../components/MinAmountInfo";
import NextImage from "next/image";
import { isReelsEnabled } from "../../../utils/featureFlags";
import NewIcon from "../../../assets/new.png";

const MediaSelectorTab = ({
  overlay,
  setFieldValue,
  children,
  voiceNoteDisabled,
  mediaDisabled,
  gifDisabled,
  imageGuessDisabled,
  onSelectionChange,
  disabled,
}) => {
  const [selected, setSelected] = React.useState(null);

  const handleSelection = (index) => {
    setSelected(index);
    onSelectionChange(index);
  };

  // Reset selection when disabled
  React.useEffect(() => {
    if (disabled && selected !== null) {
      handleSelection(null);
      setFieldValue("mediaType", null);
    }
  }, [disabled]);

  const _children = React.Children.toArray(children);
  const { media, mediashare, youtube, tiktok, reels, voice_note, image_guess } = overlay;

  return (
    <Flex direction="column">
      <Wrap columns={5} spacing={"0.5em"}>
        {/* index 0 — Image Guess */}
        {mediashare && image_guess ? (
          <WrapItem>
            <Button
              color="black"
              data-testid="image_guess-button"
              pressed={0 === selected}
              type="button"
              backgroundColor="saweria-yellow.normal"
              filter={imageGuessDisabled ? "grayscale(75%)" : "none"}
              label={
                <Flex align="center" gap={1}>
                  <span style={{marginRight: "10px"}}>Tebak Gambar</span>
                  <NextImage src={NewIcon} alt="new" width="30" height="20" />
                </Flex>
              }
              disabled={imageGuessDisabled}
              onClick={() => {
                if (imageGuessDisabled) return;
                if (selected === 0) {
                  handleSelection(null);
                  setFieldValue("mediaType", null);
                  return;
                }
                handleSelection(0);
                setFieldValue("mediaType", "image_guess");
              }}
            />
          </WrapItem>
        ) : null}

        {/* index 1 — YouTube */}
        {mediashare && youtube != false ? (
          <WrapItem flexGrow={1}>
            <Button
              flexGrow={1}
              data-testid="yt-button"
              pressed={1 === selected}
              marginBottom={0}
              type="button"
              backgroundColor="#c4302b"
              filter={mediaDisabled ? "grayscale(75%)" : "none"}
              color={"white"}
              label={"YouTube"}
              disabled={mediaDisabled}
              onClick={() => {
                if (mediaDisabled) return;
                if (selected === 1) {
                  handleSelection(null);
                  setFieldValue("mediaType", null);
                  return;
                }
                handleSelection(1);
                setFieldValue("mediaType", "yt");
              }}
            />
          </WrapItem>
        ) : null}

        {/* index 2 — Tiktok */}
        {mediashare && tiktok ? (
          <WrapItem flexGrow={1}>
            <Button
              color={"white"}
              flexGrow={1}
              data-testid="tiktok-button"
              pressed={2 === selected}
              type="button"
              backgroundColor="#080808"
              filter={mediaDisabled ? "grayscale(75%)" : "none"}
              label={"Tiktok"}
              disabled={mediaDisabled}
              onClick={() => {
                if (mediaDisabled) return;
                if (selected === 2) {
                  handleSelection(null);
                  setFieldValue("mediaType", null);
                  return;
                }
                handleSelection(2);
                setFieldValue("mediaType", "tiktok");
              }}
            />
          </WrapItem>
        ) : null}
     
      {/* index 3 — GIF */}
        {media ? (
          <WrapItem flexGrow={1}>
            <Button
              marginBottom={0}
              flexGrow={1}
              data-testid="giphy-button"
              pressed={3 === selected}
              backgroundColor="saweria-green.normal"
              filter={gifDisabled ? "grayscale(75%)" : "none"}
              type="button"
              label={"GIF"}
              disabled={gifDisabled}
              onClick={() => {
                if (gifDisabled) return;
                if (selected === 3) {
                  handleSelection(null);
                  setFieldValue("mediaType", null);
                  return;
                }
                handleSelection(3);
                setFieldValue("mediaType", "gif");
              }}
            />
          </WrapItem>
        ) : null}

        {/* index 4 — Reels */}
        {mediashare && reels && isReelsEnabled ? (
          <WrapItem>
            <Button
              color="black"
              flexGrow={1}
              data-testid="reels-button"
              pressed={4 === selected}
              type="button"
              filter={mediaDisabled ? "grayscale(75%)" : "none"}
              label={"Reels"}
              disabled={mediaDisabled}
              onClick={() => {
                if (mediaDisabled) return;
                if (selected === 4) {
                  handleSelection(null);
                  setFieldValue("mediaType", null);
                  return;
                }
                handleSelection(4);
                setFieldValue("mediaType", "reels");
              }}
            />
          </WrapItem>
        ) : null}

        {/* index 5 — Voice Note */}
        {mediashare && voice_note ? (
          <WrapItem>
            <Button
              color="black"
              flexGrow={1}
              data-testid="voice_note-button"
              pressed={5 === selected}
              type="button"
              backgroundColor="saweria-purple.normal"
              filter={voiceNoteDisabled ? "grayscale(75%)" : "none"}
              label={"Voice Note"}
              disabled={voiceNoteDisabled}
              onClick={() => {
                if (voiceNoteDisabled) return;
                if (selected === 5) {
                  handleSelection(null);
                  setFieldValue("mediaType", null);
                  return;
                }
                handleSelection(5);
                setFieldValue("mediaType", "voice_note");
              }}
            />
          </WrapItem>
        ) : null}
         </Wrap>

      <Flex paddingBottom={2} direction="column">
        {!disabled && _children[selected]}
      </Flex>
    </Flex>
  );
};

const MediaSelector = ({
  plus,
  overlay,
  values,
  setFieldValue,
  setFieldError,
  currency,
  errors,
  touched,
  disabled,
  challengeSelected,
}) => {
  if (!(plus && (overlay.media || overlay.mediashare))) {
    return null;
  }

  const belowMinMedia =
    (overlay.media || overlay.mediashare) &&
    values.amount &&
    values.amount < overlay.min_media;

  const belowMinSound =
    overlay.voice_note &&
    values.amount &&
    values.amount < overlay.min_sound;

  const belowMinImageGuess =
    overlay.image_guess &&
    values.amount &&
    values.amount < overlay.min_image_guess;

  const [hasSelection, setHasSelection] = React.useState(false);

  // a challenge only combines with GIF
  return (
    <>
      <P>
        <Trans>Media:</Trans>
      </P>
      <MediaSelectorTab
        setFieldValue={setFieldValue}
        overlay={overlay}
        voiceNoteDisabled={disabled || challengeSelected || belowMinSound}
        mediaDisabled={disabled || challengeSelected || belowMinMedia}
        gifDisabled={disabled || belowMinMedia}
        imageGuessDisabled={disabled || challengeSelected || belowMinImageGuess}
        onSelectionChange={(selected) => setHasSelection(selected !== null)}
        disabled={disabled}
      >
        {/* index 0: Image Guess */}
        <ImageGuessSelector
          currency={currency}
          overlay={overlay}
          values={values}
          setFieldValue={setFieldValue}
          setFieldError={setFieldError}
          disabled={belowMinImageGuess}
          mediaDisabled={belowMinImageGuess}
          imageGuessDisabled={belowMinImageGuess}
          errors={errors}
          touched={touched}
        />

        {/* index 1: YouTube */}
        <YTSelector
          currency={currency}
          overlay={overlay}
          values={values}
          setFieldError={setFieldError}
        />

        {/* index 2: Tiktok */}
        <TiktokSelector
          currency={currency}
          overlay={overlay}
          values={values}
          setFieldError={setFieldError}
        />

         {/* index 3: GIF */}
        <GifSelector overlay={overlay} currency={currency} />

        {/* index 4: Reels */}
        <ReelsSelector
          currency={currency}
          overlay={overlay}
          values={values}
          setFieldError={setFieldError}
        />

        {/* index 5: Voice Note */}
        <VoiceNoteSelector
          currency={currency}
          overlay={overlay}
          values={values}
          setFieldValue={setFieldValue}
          setFieldError={setFieldError}
          disabled={belowMinSound}
        />
      </MediaSelectorTab>

      {(overlay.media || overlay.voice_note || overlay.mediashare) &&
        values.amount &&
        !hasSelection &&
        (() => {
          if (belowMinSound && belowMinMedia && belowMinImageGuess) {
            return (
              <MinAmountInfo
                labels={["Min. mediashare:", "Min. voice note:", "Min. tebak gambar:"]}
                amounts={[overlay.min_media, overlay.min_sound, overlay.min_image_guess]}
                currency={currency}
                reactIcon={FaMoneyBills}
                mt={0}
                mb={5}
              />
            );
          } else if (belowMinSound) {
            return (
              <MinAmountInfo
                labels={["Min. voice note:"]}
                amounts={[overlay.min_sound]}
                currency={currency}
                reactIcon={FaMoneyBills}
                mt={0}
                mb={5}
              />
            );
          } else if (belowMinMedia) {
            return (
              <MinAmountInfo
                labels={["Min. mediashare:"]}
                amounts={[overlay.min_media]}
                currency={currency}
                reactIcon={FaMoneyBills}
                mt={0}
                mb={5}
              />
            );
          } else if (belowMinImageGuess) {
            return (
              <MinAmountInfo
                labels={["Min. tebak gambar:"]}
                amounts={[overlay.min_image_guess]}
                currency={currency}
                reactIcon={FaMoneyBills}
                mt={0}
                mb={5}
              />
            );
          }
          return null;
        })()}
    </>
  );
};

export { MediaSelector };
